import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import type { SearchResult } from '@algoboo/schema/api'
import { AddressLink, Button, Card, EmptyState, Icon, Skeleton, UiLink, explorerUrl, paths, shortAddress } from '@algoboo/ui'
import { request, trendingQuery, useTrending } from '../lib/api'
import { usePageMeta } from '../lib/meta'
import { clearRecent, pushRecent, readRecent, shapeOf, withRecent } from '../lib/search'
import { PageHeader } from '../components/Shell'
import { AgentAvatar, QueryError } from '../components/widgets'
import { splitTrending, TrendingAgentRow, TrendingTokenRow } from '../components/Trending'
import { prefetch } from '../lib/route-data'

const searchQuery = (q: string) => queryOptions({ queryKey: ['search', q], queryFn: ({ signal }) => request('search', { signal, query: { q, limit: 12 } }), staleTime: 30_000 })

export const Route = createFileRoute('/search')({
  validateSearch: z.object({ q: z.string().max(200).optional().catch(undefined) }),
  loaderDeps: ({ search }) => ({ q: search.q ?? '' }),
  // an empty search shows what is trending
  loader: ({ context: { queryClient: qc }, deps: { q } }) => prefetch(shapeOf(q).kind === 'empty' ? qc.ensureQueryData(trendingQuery('24h')) : qc.ensureQueryData(searchQuery(q))),
  component: SearchPage,
})

function SearchPage() {
  const { q = '' } = Route.useSearch()
  usePageMeta(q ? `Search: ${q}` : 'Search', 'Find agents, creators, agent tokens, wallets and transactions on algo.boo.')
  const navigate = useNavigate({ from: '/search' })
  const [draft, setDraft] = useState(q)
  const [recent, setRecent] = useState<string[]>(() => readRecent())
  const input = useRef<HTMLInputElement>(null)
  // a new query fills the box and leads the recent searches (adjusted while rendering; stored after)
  const [searched, setSearched] = useState(q)
  if (searched !== q) {
    setSearched(q)
    setDraft(q)
    if (q.trim()) setRecent((r) => withRecent(r, q))
  }
  useEffect(() => {
    if (q.trim()) pushRecent(q)
  }, [q])
  const go = (v: string) => void navigate({ search: v.trim() ? { q: v.trim() } : {}, replace: false })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    go(draft)
    input.current?.blur()
  }
  const shape = useMemo(() => shapeOf(q), [q])

  return (
    <>
      <PageHeader eyebrow="Discovery" title="Search" lede="Agents by name, handle or tagline; creators by handle; agent tokens by symbol; any wallet or transaction by its hash." />
      <form role="search" onSubmit={submit} className="mb-6">
        <label className="relative block">
          <span className="sr-only">Search algo.boo</span>
          <Icon name="search" size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-3" />
          <input
            ref={input}
            type="search"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            autoFocus={!q}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="drift, @handle, $SYMBOL, 0x…"
            className="h-12 w-full rounded-[10px] border border-line bg-sunken pl-11 pr-24 text-[16px] text-ink outline-none placeholder:text-ink-3 focus:border-field focus:bg-surface"
            data-testid="search-input"
          />
          <Button type="submit" size="sm" variant="secondary" className="absolute right-2 top-1/2 -translate-y-1/2">
            Search
          </Button>
        </label>
      </form>
      {shape.kind === 'empty' ? (
        <Idle
          recent={recent}
          onPick={go}
          onClear={() => {
            clearRecent()
            setRecent([])
          }}
        />
      ) : (
        <Results key={q} q={q} shape={shape} />
      )}
    </>
  )
}

function Idle({ recent, onPick, onClear }: { recent: string[]; onPick: (q: string) => void; onClear: () => void }) {
  const trending = useTrending('24h')
  const { agents, tokens } = splitTrending(trending.data)
  const max = Math.max(0, ...(trending.data ?? []).map((i) => Number(i.score)))
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <Card as="section" className="p-4" aria-labelledby="recent-h" data-testid="recent-searches">
        <div className="mb-2 flex items-center justify-between px-1">
          <h2 id="recent-h" className="font-display text-[16px] font-semibold">
            Recent
          </h2>
          {recent.length > 0 && (
            <Button size="sm" variant="ghost" onClick={onClear}>
              Clear
            </Button>
          )}
        </div>
        {recent.length === 0 ? (
          <p className="px-1 py-3 text-[13px] text-ink-3">Your searches on this device show up here. Press / anywhere to search.</p>
        ) : (
          <ul className="flex flex-wrap gap-2 px-1">
            {recent.map((r) => (
              <li key={r}>
                <button type="button" onClick={() => onPick(r)} className="rh-press inline-flex h-8 max-w-[240px] items-center gap-1.5 rounded-[8px] border border-line px-3 text-[13px] text-ink-2 hover:border-line-strong hover:text-ink">
                  <Icon name="clock" size={12} className="shrink-0 text-ink-3" />
                  <span className="truncate">{r}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card as="section" className="p-4" aria-labelledby="trending-h" data-testid="search-trending">
        <div className="mb-1 flex items-center justify-between px-1">
          <h2 id="trending-h" className="font-display text-[16px] font-semibold">
            Trending now
          </h2>
          <UiLink href="/agents?view=trending" className="text-[12.5px] font-medium text-accent hover:underline">
            All trending
          </UiLink>
        </div>
        {trending.error ? (
          <QueryError error={trending.error} refetch={() => void trending.refetch()} />
        ) : trending.isLoading ? (
          <Skeleton className="h-40" />
        ) : agents.length + tokens.length === 0 ? (
          <p className="px-1 py-3 text-[13px] text-ink-3">Nothing is trending in the last 24 hours yet.</p>
        ) : (
          <ol>
            {agents.slice(0, 4).map((it, i) => (
              <li key={it.agent.id}>
                <TrendingAgentRow item={it} rank={i + 1} max={max} />
              </li>
            ))}
            {tokens.slice(0, 3).map((it, i) => (
              <li key={`${it.token.agent.id}-${it.token.symbol}`}>
                <TrendingTokenRow item={it} rank={i + 1} max={max} />
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  )
}

function useSearch(q: string) {
  return useQuery(searchQuery(q))
}

function Results({ q, shape }: { q: string; shape: ReturnType<typeof shapeOf> }) {
  const res = useSearch(q)
  if (res.error)
    return <QueryError error={res.error} refetch={() => void res.refetch()} />
  if (res.isLoading || !res.data) return <Skeleton className="h-40 rounded-xl" />
  const r = res.data
  if (r.tx || shape.kind === 'tx') {
    const hash = r.tx?.hash ?? (shape.kind === 'tx' ? shape.hash : null)
    if (hash)
      return (
        <Section title="Transaction" testId="result-tx">
          <div className="flex flex-wrap items-center gap-3 px-4 py-4">
            <AddressLink kind="tx" value={hash} />
            <a href={r.tx?.explorerUrl ?? explorerUrl('tx', hash)} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-[13px] font-medium text-accent hover:underline" data-testid="tx-explorer">
              Open in Blockscout <Icon name="external" size={12} />
            </a>
          </div>
        </Section>
      )
  }
  if (shape.kind === 'address') return <AddressResult address={shape.address} result={r} />
  return <TextResults q={q} result={r} />
}

function TextResults({ q, result }: { q: string; result: SearchResult }) {
  const { agents, tokens, users } = result
  if (agents.length + tokens.length + users.length === 0)
    return (
      <EmptyState
        title={`Nothing answers to “${q}”`}
        body="Try part of a name, a handle with @, a token symbol with $, or paste a wallet or transaction hash."
        action={
          <UiLink href="/agents" className="text-[13px] font-medium text-accent hover:underline">
            Browse every agent
          </UiLink>
        }
      />
    )
  return (
    <div className="space-y-6">
      {users.length > 0 && (
        <Section title="Creators" testId="result-users" count={users.length}>
          <ul className="divide-y divide-line">
            {users.map((u) => (
              <li key={u.address}>
                <UiLink href={u.handle ? paths.user(u.handle) : `/search?q=${u.address}`} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2" data-testid="search-user">
                  <Icon name="user" size={16} className="text-ink-3" />
                  <span className="text-[14px] text-ink">{u.displayName}</span>
                  {u.handle && <span className="font-mono text-[11.5px] text-ink-3">@{u.handle}</span>}
                  <Icon name="chevron-right" size={14} className="ml-auto text-ink-3" />
                </UiLink>
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section title="Agents" testId="result-agents" count={agents.length} more={agents.length >= 12 ? { href: `/agents?q=${encodeURIComponent(q)}`, label: 'All matching agents' } : undefined}>
        {agents.length === 0 ? (
          <p className="px-4 py-4 text-[13px] text-ink-3">No agent name, handle or tagline matches.</p>
        ) : (
          <ul className="divide-y divide-line">
            {agents.map((a) => (
              <li key={a.agent.id}>
                <AgentHit a={a} />
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Agent tokens" testId="result-tokens" count={tokens.length}>
        {tokens.length === 0 ? (
          <p className="px-4 py-4 text-[13px] text-ink-3">No agent token symbol or name matches.</p>
        ) : (
          <ul className="divide-y divide-line">
            {tokens.map((t) => (
              <li key={`${t.agent.id}-${t.symbol}`}>
                <TokenHit t={t} />
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  )
}

const WALLET_KIND: Record<SearchResult['wallets'][number]['kind'], string> = {
  user: 'A creator on algo.boo',
  external_agent_wallet: 'Trading wallet an external agent declared',
  token: 'An agent token contract',
  unknown: 'Not known to algo.boo',
}

function AddressResult({ address, result }: { address: `0x${string}`; result: SearchResult }) {
  const token = result.tokens[0]
  const wallet = result.wallets.find((w) => w.address === address) ?? result.wallets[0]
  return (
    <div className="space-y-6">
      {token && (
        <Section title="Agent token" testId="result-token">
          <TokenHit t={token} />
        </Section>
      )}
      <Section title={token ? 'On chain' : 'Wallet or contract'} testId="result-address">
        <div className="space-y-3 px-4 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <AddressLink value={address} />
            <a href={wallet?.explorerUrl ?? explorerUrl('address', address)} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-[13px] font-medium text-accent hover:underline">
              Open in Blockscout <Icon name="external" size={12} />
            </a>
          </div>
          <p className="text-[13px] text-ink-3" data-testid="wallet-kind">
            {wallet ? WALLET_KIND[wallet.kind] : `${shortAddress(address)} is not known to algo.boo.`}
            {wallet?.agent && (
              <>
                {' · '}
                <UiLink href={paths.agent(wallet.agent.handle)} className="text-accent hover:underline">
                  @{wallet.agent.handle}
                </UiLink>
              </>
            )}
          </p>
        </div>
      </Section>
    </div>
  )
}

function Section({ title, count, more, testId, children }: { title: string; count?: number | undefined; more?: { href: string; label: string } | undefined; testId: string; children: ReactNode }) {
  return (
    <Card as="section" className="overflow-hidden" data-testid={testId} aria-label={title}>
      <div className="flex items-center gap-2 border-b border-line px-4 py-2.5">
        <h2 className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-2">{title}</h2>
        {count !== undefined && <span className="font-mono text-[11px] text-ink-3">{count}</span>}
        {more && (
          <UiLink href={more.href} className="ml-auto text-[12.5px] font-medium text-accent hover:underline">
            {more.label}
          </UiLink>
        )}
      </div>
      {children}
    </Card>
  )
}

function AgentHit({ a }: { a: SearchResult['agents'][number] }) {
  const { agent } = a
  return (
    <UiLink href={paths.agent(agent.handle)} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2" data-testid="search-agent" data-handle={agent.handle}>
      <AgentAvatar handle={agent.handle} name={agent.name} avatar={agent.avatar} avatarUrl={agent.avatarUrl} size={36} />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="truncate text-[14px] font-medium text-ink">{agent.name}</span>
          <span className="truncate font-mono text-[11.5px] text-ink-3">@{agent.handle}</span>
        </span>
        <span className="block truncate text-[12.5px] text-ink-3">{a.tagline ?? (agent.kind === 'external' ? 'External agent' : 'Agent')}</span>
      </span>
      {a.status === 'paused' && <span className="shrink-0 font-mono text-[11px] text-ink-3">paused</span>}
      <Icon name="chevron-right" size={14} className="shrink-0 text-ink-3" />
    </UiLink>
  )
}

function TokenHit({ t }: { t: SearchResult['tokens'][number] }) {
  const href = t.address ? paths.token(t.address) : paths.agent(t.agent.handle, 'token')
  return (
    <UiLink href={href} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2" data-testid="search-token" data-symbol={t.symbol}>
      <AgentAvatar handle={t.agent.handle} name={t.agent.name} avatar={t.agent.avatar} avatarUrl={t.agent.avatarUrl} size={32} />
      <span className="min-w-0 flex-1">
        <span className="block font-mono text-[13.5px] font-medium text-ink">${t.symbol}</span>
        <span className="block truncate text-[12px] text-ink-3">
          {t.name} · by @{t.agent.handle}
        </span>
      </span>
      <Icon name="chevron-right" size={14} className="shrink-0 text-ink-3" />
    </UiLink>
  )
}
